import {z} from 'zod'
import dayjs from 'dayjs'
import {
    BarthelScaleQuestionsPool,
    Diagnosis,
    DisabilityProfileIcfCode,
    EvaluationScaleBase,
    MedicalDevice,
    NetworkSupport,
    MobilityAutonomy,
    ProjectByPatient,
    ProjectEndingReason,
    ProjectNotesType,
    ProjectPathology,
    ScaleType,
    Applicant,
    ActivityAutonomy,
    ParentalResponsibility,
    ProjectClinicalPresentation,
    StabilityLevel,
    ProjectClinicalCareNeed,
    ProjectPatientLivingCondition,
    ProjectRehabilitationFacility,
    ProjectStatus,
    IadlScaleQuestionsPool,
    ProjectTeamMember,
    ProjectSuspendReason,
    PrintSiarCategorySection,
    DisabilityProfile
} from './types'
import i18n from '@/translations/i18n'
import {AssistiveRegimeEnum, ProjectCycleCount, ProjectSender} from '@/features/projects/types'
import {PatientSmall} from '@/features/patients/types'
import {UploadedDocument} from '@/types'
import {NotificationUsersConfirmationValidationSchema} from '../users/types'
import {ReferringSpecialist} from '@/features/referring-specialists/types'

export const NOTES_MAX_LENGTH = 500

export const ProjectFieldsSchema = z
    .object({
        id: z.string().min(1, {message: i18n.t('errors:required')}),
        treatmentRequestId: z.string().optional(),
        status: z.enum(ProjectStatus.options).default('open'),
        patient: z.object({
            id: z.string().min(1),
            fullName: z.string().min(1),
            serviceType: z.enum(PatientSmall.shape.serviceType.options)
        }),
        projectType: z.enum(['continuous', 'cyclic'], {required_error: i18n.t('errors:required')}),
        numberOfCycles: z.enum(ProjectCycleCount.options, {
            required_error: i18n.t('errors:required'),
            invalid_type_error: i18n.t('errors:required')
        }),
        sector: z.enum(['outpatient', 'domiciliary', 'mixed'], {
            errorMap: issue => {
                if (issue.code == 'invalid_enum_value' || issue.code == 'invalid_type') {
                    return {message: i18n.t('projects:sector_error_invalid_type')}
                }

                return {message: i18n.t('errors:required')}
            }
        }),
        assistiveRegime: AssistiveRegimeEnum.nullish(),
        startDate: z.date({
            required_error: i18n.t('errors:required'),
            invalid_type_error: i18n.t('errors:invalid_date')
        }),
        expirationDate: z.date({
            invalid_type_error: i18n.t('errors:invalid_date'),
            required_error: i18n.t('errors:required')
        }),
        durationDays: z
            .number({required_error: i18n.t('errors:required'), invalid_type_error: i18n.t('errors:required')})
            .int({message: i18n.t('errors:required')})
            .or(z.literal('')),
        sender: z.enum(ProjectSender.options),
        specialist: z.object(ReferringSpecialist.shape),
        endDate: z.date({invalid_type_error: i18n.t('errors:required'), required_error: i18n.t('errors:required')}),
        siarProject: z.enum(['same_as_previous', 'empty'], {required_error: i18n.t('errors:required')}),
        endingReason: z.enum(ProjectEndingReason.options, {required_error: i18n.t('errors:required')}),
        referringProject: z.object(
            ProjectByPatient.pick({
                id: true,
                code: true,
                type: true,
                startDate: true,
                expirationDate: true,
                sector: true,
                durationDays: true
            }).shape
        ),
        // notes
        aslFirstAttachment: UploadedDocument.nullish(),
        aslSecondAttachment: UploadedDocument.nullish(),
        notesDescription: z.string().max(NOTES_MAX_LENGTH).nullish(),
        notesType: ProjectNotesType.nullish(),
        // diagnoses and facilities
        mainPathologies: z.array(
            ProjectPathology.pick({startedAt: true, diagnosisId: true}).merge(
                Diagnosis.pick({description: true, code: true})
            )
        ),
        otherPathologies: z.array(
            ProjectPathology.pick({diagnosisId: true}).merge(Diagnosis.pick({description: true, code: true}))
        ),
        devices: z.array(MedicalDevice.pick({device: true})),
        // social welfare conditions
        parentalResponsibility: ParentalResponsibility.nullish(),
        applicant: Applicant,
        percentagePaid: z.coerce.number().min(0).max(100),
        sharedCarePlanning: z.boolean(),
        socialRehabilitationTreatments: z.boolean(),
        networkSupport: NetworkSupport,
        caregiver: z.boolean(),
        activityAutonomy: ActivityAutonomy,
        mobilityAutonomy: MobilityAutonomy,
        // clinical presentation
        cognitiveDisorders: ProjectClinicalPresentation,
        behavioralDisorders: ProjectClinicalPresentation,
        communicationDisorders: ProjectClinicalPresentation,
        sensoryDeficit: ProjectClinicalPresentation,
        stabilityLevel: StabilityLevel,
        careNeeds: z.array(
            z.object({
                name: ProjectClinicalCareNeed
            })
        ),
        // patient additional info
        livingConditions: ProjectPatientLivingCondition.nullish(),
        rehabilitationFacility: ProjectRehabilitationFacility.nullish(),
        // team
        team: ProjectTeamMember.pick({id: true, fullName: true, group: true, specializationName: true}).array(),
        // suspend / resume project
        suspensionStartDate: z.date({
            invalid_type_error: i18n.t('errors:required'),
            required_error: i18n.t('errors:required')
        }),
        suspensionEndDate: z.date({
            invalid_type_error: i18n.t('errors:required'),
            required_error: i18n.t('errors:required')
        }),
        suspensionReason: z.enum(ProjectSuspendReason.options, {required_error: i18n.t('errors:required')}),
        suspensionNotes: z.string().max(NOTES_MAX_LENGTH).nullish(),
        // used for early closing project
        hasReopening: z.boolean().default(false)
    })
    .merge(NotificationUsersConfirmationValidationSchema)
export type ProjectFieldsSchema = z.infer<typeof ProjectFieldsSchema>

// Create form validation schema
const ProjectContinuousFormSchema = z.discriminatedUnion('projectType', [
    z.object({projectType: z.literal('continuous')})
])

const ProjectCyclicFormSchema = z.discriminatedUnion('projectType', [
    z.object({projectType: z.literal('cyclic')}).merge(
        z.object({
            numberOfCycles: ProjectFieldsSchema.shape.numberOfCycles.optional()
        })
    )
])

const ProjectUndefinedSchema = z.discriminatedUnion('projectType', [z.object({projectType: z.undefined()})])

const ProjectNullableDurationFormSchema = z.object({
    durationDays: z.number().int().or(z.literal('')).nullable()
})

const ProjectNullableSenderSpecialistFormSchema = z.object({
    sender: ProjectSender.nullable(),
    specialist: ReferringSpecialist.nullable()
})

export const CreateProjectFormValidationSchema = ProjectFieldsSchema.pick({
    treatmentRequestId: true,
    status: true,
    patient: true,
    sector: true,
    assistiveRegime: true,
    startDate: true,
    expirationDate: true
})
    .and(ProjectNullableSenderSpecialistFormSchema.partial())
    .and(ProjectNullableDurationFormSchema.partial())
    .and(
        z.discriminatedUnion('projectType', [
            ...ProjectUndefinedSchema.options,
            ...ProjectCyclicFormSchema.options,
            ...ProjectContinuousFormSchema.options
        ])
    )
export type CreateProjectFormValidationSchema = z.infer<typeof CreateProjectFormValidationSchema>

export const CreateProjectConfirmationFormSchema = CreateProjectFormValidationSchema.and(
    ProjectFieldsSchema.pick({
        notificationsRecipients: true,
        notificationDetails: true
    })
)
export type CreateProjectConfirmationFormSchema = z.infer<typeof CreateProjectConfirmationFormSchema>

export const RenewProjectFormValidationSchema = ProjectFieldsSchema.pick({
    id: true,
    patient: true,
    referringProject: true,
    sector: true,
    assistiveRegime: true,
    startDate: true,
    expirationDate: true,
    durationDays: true,
    sender: true,
    specialist: true
})
export type RenewProjectFormValidationSchema = z.infer<typeof RenewProjectFormValidationSchema>

export const RenewProjectConfirmationFormSchema = RenewProjectFormValidationSchema.and(
    ProjectFieldsSchema.pick({
        notificationsRecipients: true,
        notificationDetails: true
    })
)
export type RenewProjectConfirmationFormSchema = z.infer<typeof RenewProjectConfirmationFormSchema>

export const TransformProjectFormValidationSchema = ProjectFieldsSchema.pick({
    id: true,
    patient: true,
    referringProject: true,
    siarProject: true,
    sector: true,
    assistiveRegime: true,
    startDate: true,
    expirationDate: true,
    durationDays: true,
    specialist: true,
    sender: true
})
export type TransformProjectFormValidationSchema = z.infer<typeof TransformProjectFormValidationSchema>

export const ResumeProjectFormValidationSchema = ProjectFieldsSchema.pick({
    id: true,
    treatmentRequestId: true,
    projectType: true,
    patient: true,
    referringProject: true,
    siarProject: true,
    sector: true,
    assistiveRegime: true,
    startDate: true,
    expirationDate: true
})
    .and(ProjectNullableSenderSpecialistFormSchema)
    .and(ProjectNullableDurationFormSchema)
export type ResumeProjectFormValidationSchema = z.infer<typeof ResumeProjectFormValidationSchema>

export const EarlyClosingProjectFormValidationSchema = ProjectFieldsSchema.pick({
    id: true,
    endDate: true,
    hasReopening: true,
    referringProject: true
})
    .and(
        z.discriminatedUnion('hasReopening', [
            z.object({
                hasReopening: z.literal(false),
                endingReason: z.enum(ProjectEndingReason.options, {required_error: i18n.t('errors:required')})
            }),
            z.object({hasReopening: z.literal(true)}).merge(
                ProjectFieldsSchema.pick({
                    patient: true,
                    referringProject: true,
                    siarProject: true,
                    sector: true,
                    assistiveRegime: true,
                    startDate: true,
                    expirationDate: true,
                    durationDays: true,
                    sender: true,
                    specialist: true
                })
            )
        ])
    )
    .superRefine((data, ctx) => {
        if (data.endDate && data.referringProject?.expirationDate) {
            if (dayjs(data.endDate).isAfter(dayjs(data.referringProject.expirationDate), 'day')) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['endDate'],
                    message: i18n.t('projects:early_closing_date_after_expiration')
                })
            }
        }
    })
export type EarlyClosingProjectFormValidationSchema = z.infer<typeof EarlyClosingProjectFormValidationSchema>

export const EarlyClosingProjectConfirmationFormValidationSchema = EarlyClosingProjectFormValidationSchema.and(
    ProjectFieldsSchema.pick({
        notificationsRecipients: true,
        notificationDetails: true
    })
)
export type EarlyClosingProjectConfirmationFormValidationSchema = z.infer<
    typeof EarlyClosingProjectConfirmationFormValidationSchema
>

export const EarlyClosingProjectConfirmationFormSchema = EarlyClosingProjectFormValidationSchema.and(
    ProjectFieldsSchema.pick({
        notificationsRecipients: true,
        notificationDetails: true
    })
)
export type EarlyClosingProjectConfirmationFormSchema = z.infer<typeof EarlyClosingProjectConfirmationFormSchema>

export const ProjectReopeningFormValidationSchema = ProjectFieldsSchema.pick({
    id: true,
    patient: true,
    referringProject: true,
    siarProject: true,
    sector: true,
    assistiveRegime: true,
    startDate: true,
    expirationDate: true,
    durationDays: true,
    sender: true,
    specialist: true
})
export type ProjectReopeningFormValidationSchema = z.infer<typeof ProjectReopeningFormValidationSchema>

export const ExtendsProjectFormValidationSchema = ProjectFieldsSchema.pick({
    id: true,
    patient: true,
    referringProject: true,
    sector: true,
    assistiveRegime: true,
    expirationDate: true,
    startDate: true,
    durationDays: true,
    sender: true,
    specialist: true
})
export type ExtendsProjectFormValidationSchema = z.infer<typeof ExtendsProjectFormValidationSchema>

// Evaluation scale form schema
export const ChooseScaleFormSchema = EvaluationScaleBase.pick({type: true})
    .and(
        z.object({
            evaluationDate: z.date({required_error: i18n.t('errors:required')}),
            totalScore: z
                .number({
                    required_error: i18n.t('projects:invalid_total_score'),
                    invalid_type_error: i18n.t('projects:invalid_total_score')
                })
                .int({message: i18n.t('projects:invalid_total_score')})
                .min(0, {message: i18n.t('projects:invalid_total_score')})
                .max(999, {message: i18n.t('projects:invalid_total_score')})
                .nullable()
        })
    )
    .superRefine((data, ctx) => {
        if (
            data.totalScore == null &&
            data.type &&
            data.type != ScaleType.enum.barthel_index &&
            data.type != ScaleType.enum.lawton_iadl
        ) {
            ctx.addIssue({
                code: 'custom',
                message: i18n.t('errors:required'),
                path: ['totalScore']
            })
        }
    })
export type ChooseScaleFormSchema = z.infer<typeof ChooseScaleFormSchema>

export const StoreBarthelEvaluationScaleFormSchema = z.object({
    type: z.literal(ScaleType.enum.barthel_index),
    questions: BarthelScaleQuestionsPool
})
export type StoreBarthelEvaluationScaleFormSchema = z.infer<typeof StoreBarthelEvaluationScaleFormSchema>

export const StoreIadlEvaluationScaleFormSchema = z.object({
    type: z.literal(ScaleType.enum.lawton_iadl),
    questions: IadlScaleQuestionsPool
})
export type StoreIadlEvaluationScaleFormSchema = z.infer<typeof StoreIadlEvaluationScaleFormSchema>

export const NotesFormSchema = ProjectFieldsSchema.pick({
    aslFirstAttachment: true,
    aslSecondAttachment: true,
    notesDescription: true,
    notesType: true
})
export type NotesFormSchema = z.infer<typeof NotesFormSchema>

export const MainPathologiesFormSchema = ProjectFieldsSchema.pick({
    mainPathologies: true
})
export type MainPathologiesFormSchema = z.infer<typeof MainPathologiesFormSchema>

export const OtherPathologiesFormSchema = ProjectFieldsSchema.pick({
    otherPathologies: true
})
export type OtherPathologiesFormSchema = z.infer<typeof OtherPathologiesFormSchema>

export const MedicalDevicesFormSchema = ProjectFieldsSchema.pick({
    devices: true
})
export type MedicalDevicesFormSchema = z.infer<typeof MedicalDevicesFormSchema>

export const SocialWelfareConditionsFormSchema = ProjectFieldsSchema.pick({
    parentalResponsibility: true,
    applicant: true,
    percentagePaid: true,
    sharedCarePlanning: true,
    socialRehabilitationTreatments: true,
    networkSupport: true,
    caregiver: true,
    activityAutonomy: true,
    mobilityAutonomy: true
})
export type SocialWelfareConditionsFormSchema = z.infer<typeof SocialWelfareConditionsFormSchema>

export const ClinicalPresentationFormSchema = ProjectFieldsSchema.pick({
    cognitiveDisorders: true,
    behavioralDisorders: true,
    communicationDisorders: true,
    sensoryDeficit: true,
    stabilityLevel: true,
    careNeeds: true
})
export type ClinicalPresentationFormSchema = z.infer<typeof ClinicalPresentationFormSchema>

export const DisabilityProfileLetterCardFormSchema = z.object({
    values: z
        .array(
            z
                .object({
                    icfCode: DisabilityProfileIcfCode.optional(),
                    type: DisabilityProfile.shape.type.optional(),
                    qualifier1: z
                        .string()
                        .min(1, {message: i18n.t('errors:required')})
                        .optional(),
                    qualifier2: z
                        .string()
                        .min(1, {message: i18n.t('errors:required')})
                        .optional()
                })
                .superRefine((values, ctx) => {
                    if (values.icfCode) {
                        if (!values.qualifier1) {
                            ctx.addIssue({
                                code: 'custom',
                                message: i18n.t('errors:required'),
                                path: ['qualifier1']
                            })
                        }
                        if ((values.icfCode.startsWith('s') || values.icfCode.startsWith('d')) && !values.qualifier2) {
                            ctx.addIssue({
                                code: 'custom',
                                message: i18n.t('errors:required'),
                                path: ['qualifier2']
                            })
                        }
                        if (values.icfCode.startsWith('e') && !values.type) {
                            ctx.addIssue({
                                code: 'custom',
                                message: i18n.t('errors:required'),
                                path: ['type']
                            })
                        }
                    }
                })
        )
        .min(1)
        .superRefine((values, ctx) => {
            if (values.filter(value => value.icfCode).length == 0) {
                ctx.addIssue({
                    code: 'custom',
                    message: i18n.t('errors:required'),
                    path: ['0.icfCode']
                })
            }
        })
})
export type DisabilityProfileLetterCardFormSchema = z.infer<typeof DisabilityProfileLetterCardFormSchema>

// Edit project details form schema
export const EditProjectDetailsFormValidationSchema = ProjectFieldsSchema.pick({
    id: true,
    sector: true,
    startDate: true,
    assistiveRegime: true,
    expirationDate: true
}).and(
    z.object({
        projectType: z.enum(['continuous', 'cyclic']).nullish(),
        durationDays: z.number().int().or(z.literal('')).nullable(),
        numberOfCycles: z.enum(ProjectCycleCount.options).nullish()
    })
)
export type EditProjectDetailsFormValidationSchema = z.infer<typeof EditProjectDetailsFormValidationSchema>

// Edit project sender form schema
export const EditReferringSpecialistValidationSchema = ProjectFieldsSchema.pick({
    id: true
}).and(
    z.object({
        sender: z.enum(ProjectSender.options).nullable(),
        specialist: z.object(ReferringSpecialist.shape).nullable()
    })
)
export type EditReferringSpecialistValidationSchema = z.infer<typeof EditReferringSpecialistValidationSchema>

// Edit project patient form schema
export const EditProjectPatientValidationSchema = ProjectFieldsSchema.pick({
    id: true,
    livingConditions: true,
    rehabilitationFacility: true
})
export type EditProjectPatientValidationSchema = z.infer<typeof EditProjectPatientValidationSchema>

// Edit project team form schema
export const EditProjectTeamValidationSchema = ProjectFieldsSchema.pick({
    id: true,
    team: true
})
export type EditProjectTeamValidationSchema = z.infer<typeof EditProjectTeamValidationSchema>

export const SuspendProjectFormValidationSchema = ProjectFieldsSchema.pick({
    suspensionStartDate: true,
    suspensionEndDate: true,
    suspensionReason: true,
    suspensionNotes: true,
    notificationsRecipients: true,
    notificationDetails: true
}).extend({
    suspensionEndDate: z
        .date({
            invalid_type_error: i18n.t('errors:invalid_date')
        })
        .optional()
})
export type SuspendProjectFormValidationSchema = z.infer<typeof SuspendProjectFormValidationSchema>

export const LiftSuspensionProjectFormValidationSchema = ProjectFieldsSchema.pick({
    suspensionEndDate: true,
    suspensionNotes: true,
    notificationsRecipients: true,
    notificationDetails: true
})
export type LiftSuspensionProjectFormValidationSchema = z.infer<typeof LiftSuspensionProjectFormValidationSchema>

// Print Siar form schema
export const PrintSiarFormSchema = z.object({
    projectId: z.string().min(1, {message: i18n.t('errors:required')}),
    categorySection: z.enum(PrintSiarCategorySection.options),
    hasSignature: z.boolean().refine(value => value, {message: i18n.t('errors:required')})
})
export type PrintSiarFormSchema = z.infer<typeof PrintSiarFormSchema>
